import React, { useMemo } from 'react';
import { Button, Grid, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from "react-router-dom";
import { CardTypes } from 'ts-interfaces';

// Components
import { Bar } from '../../auth/Bar';
import { ProjectSelector } from './components/ProjectSelector';
import { ProjectDetailsPanel } from './components/ProjectDetailsPanel';
import { BoardsPanel } from './components/BoardsPanel';
import { CollectionsPanel } from './components/CollectionsPanel';
import { SelectionModal, ChoiceModal } from './components/ModalComponents';
import { PeopleWithAccess } from '../components/PeopleWithAccess';
import { ProjectsSnackbarComponent } from '../components/ProjectsSnackbarComponent';
import { BoardScheme } from '../boards_scheme/BoardScheme';
import { CollectionScheme } from '../collections_scheme/CollectionScheme';

// Hooks
import { useProjectManagement } from './hooks/useProjectManagement';
import { useBoardManagement } from './hooks/useBoardManagement';
import { useCollectionManagement } from './hooks/useCollectionManagement';
import { useTeamsProAccess } from '../../hooks/useTeamsProAccess';

interface ProjectSchemeManagementProps {
  selectedProject: any;
  designFirms: any[];
  teamMembers: any[];
  associatedProjectsList: any[];
  permissions: any;
  hasViewerAccess: boolean;
}

export const ProjectSchemeManagement = ({ 
  selectedProject, 
  designFirms, 
  teamMembers, 
  associatedProjectsList, 
  permissions,
  hasViewerAccess 
}: ProjectSchemeManagementProps) => {
  const navigate = useNavigate();

  // Project management hook
  const {
    isEditing,
    isSaving,
    editedProject,
    projectData,
    ownedProjects,
    sharedProjects,
    handleInputChange,
    handleSave,
    handleCancel,
    handleEdit,
    setBoards,
    setCollections,
    currentUser,
    projectId,
    setSnackbarMessage
  } = useProjectManagement(selectedProject, permissions);

  const { isTeamsProUser, isRegularProUser } = useTeamsProAccess();

  // Destructure with defaults to avoid null/undefined issues
  const { project, boards, collections, isLoading } = projectData || {
    project: {} as any,
    boards: [],
    collections: [],
    isLoading: false
  };

  const filteredCollections = useMemo(() => {
    return collections.filter((collection) => {
      // Teams PRO users can access all collections
      if (isTeamsProUser === true) {
        return true;
      }
      
      // Regular PRO users can access only their own collections
      if (isRegularProUser === true) {
        return collection.uid === currentUser?.uid;
      }
      
      // For non-PRO users, show all collections they own
      return collection.uid === currentUser?.uid;
    });
  }, [collections, isTeamsProUser, isRegularProUser, currentUser?.uid]);

  // Board management hook
  const {
    openBoardModal,
    selectedBoardIds,
    openBoardChoiceModal,
    handleOpenBoardModal,
    handleCloseBoardModal,
    handleCloseBoardChoiceModal,
    handleAddExistingBoard,
    handleCreateNewBoard,
    handleBoardSelectionChange,
    handleSaveBoardAssociations,
    handleBoardUpdate,
  } = useBoardManagement(
    projectId,
    boards,
    setBoards,
    permissions,
    setSnackbarMessage
  );

  // Collection management hook
  const {
    openCollectionModal,
    selectedCollectionIds,
    openCollectionChoiceModal,
    handleOpenCollectionModal,
    handleCloseCollectionModal,
    handleCloseCollectionChoiceModal,
    handleAddExistingCollection,
    handleCreateNewCollection,
    handleCollectionSelectionChange,
    handleSaveCollectionAssociations,
    handleCollectionUpdate,
  } = useCollectionManagement(
    projectId,
    filteredCollections,
    setCollections,
    permissions,
    setSnackbarMessage,
    associatedProjectsList
  );

  const handleBackToProjects = () => {
    navigate('/projects/projects');
  };

  if (!selectedProject) {
    return (
      <>
        <Bar />
        <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px' }}>
          <div>No project selected</div>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Bar />
      <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginBottom: '20px', padding: '32px' }}>
        <Grid sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Grid sx={{ width: '320px' }}>
            <Typography sx={{ fontWeight: 'light', mb: 0.5, ml: 0.5, fontSize: '0.75rem', color: '#666666' }}>Select Project</Typography>
            <ProjectSelector
              selectedProject={selectedProject}
              ownedProjects={ownedProjects}
              sharedProjects={sharedProjects}
            />
          </Grid>
          <Button
            startIcon={<ArrowBackIcon />}
            sx={{ 
              color: 'black', 
              textTransform: 'none', 
              fontWeight: 'bold',
              fontSize: '1rem',
              width: '200px',
            }}
            onClick={handleBackToProjects}
          >
            Back to Projects
          </Button>
        </Grid>

        <Grid container sx={{ display: 'flex', flexDirection: 'row' }} spacing={2}>
          {/* Project Details Panel */}
          <Grid item sx={{ minWidth: '336px', flex: 0.5 }}>
            <Grid sx={{ display: 'flex', flexDirection: 'column', height: '786px' }}>
              <ProjectDetailsPanel
                selectedProject={selectedProject}
                editedProject={editedProject}
                isEditing={isEditing}
                isSaving={isSaving}
                hasViewerAccess={hasViewerAccess}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancel={handleCancel}
                onInputChange={handleInputChange}
              />
              {designFirms?.length > 0 && teamMembers?.length > 0 && isTeamsProUser === true && (
                <Paper sx={{ p: 3, mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <PeopleWithAccess 
                    resourceId={selectedProject.id} 
                    resourceName={selectedProject.name} 
                    resourceType={CardTypes.Projects} 
                    resourceOwner={selectedProject.owner} 
                    teamID={designFirms[0]} 
                    hasViewerAccess={hasViewerAccess}
                    isTeamsProUser={isTeamsProUser || false}
                    isRegularProUser={isRegularProUser || false}
                  />
                </Paper>
              )}
            </Grid>
          </Grid>

          {/* Boards Panel */}
          <Grid item sx={{ flex: 1, minWidth: '400px' }}>
            <BoardsPanel
              boards={boards}
              designFirms={designFirms}
              teamMembers={teamMembers}
              permissions={permissions}
              onBoardUpdate={handleBoardUpdate}
              onOpenModal={handleOpenBoardModal}
              hasViewerAccess={hasViewerAccess}
            />
          </Grid>

          {/* Collections Panel */}
          <Grid item sx={{ flex: 1, minWidth: '400px' }}>
            <CollectionsPanel
              collections={filteredCollections}
              designFirms={designFirms}
              teamMembers={teamMembers}
              permissions={permissions}
              associatedProjectsList={associatedProjectsList}
              hasViewerAccess={hasViewerAccess}
              onCollectionUpdate={handleCollectionUpdate}
              onOpenModal={handleOpenCollectionModal}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Board Choice Modal */}
      <ChoiceModal
        open={openBoardChoiceModal}
        onClose={handleCloseBoardChoiceModal}
        title="Keep your boards together in a project."
        onCreateNew={handleCreateNewBoard}
        onAddExisting={handleAddExistingBoard}
        createNewText="Create a New Board"
        addExistingText="Add an Existing Board"
      />

      {/* Collection Choice Modal */}
      <ChoiceModal
        open={openCollectionChoiceModal}
        onClose={handleCloseCollectionChoiceModal}
        title="Keep your collections together in a project."
        onCreateNew={handleCreateNewCollection}
        onAddExisting={handleAddExistingCollection}
        createNewText="Create New Collection"
        addExistingText="Add Existing Collection"
      />

      {/* Board Selection Modal */}
      <SelectionModal
        open={openBoardModal}
        onClose={handleCloseBoardModal}
        onSave={handleSaveBoardAssociations}
        title="Browse Boards"
      >
        <BoardScheme 
          designFirms={designFirms}
          teamMembers={teamMembers}
          showCheckboxes={true}
          selectedBoardIds={selectedBoardIds}
          onBoardSelectionChange={handleBoardSelectionChange}
          selectedProjectId={projectId}
          allProjects={[...ownedProjects, ...sharedProjects]}
        />
      </SelectionModal>

      {/* Collection Selection Modal */}
      <SelectionModal
        open={openCollectionModal}
        onClose={handleCloseCollectionModal}
        onSave={handleSaveCollectionAssociations}
        title="Browse Collections"
      >
        <CollectionScheme 
          designFirms={designFirms}
          teamMembers={teamMembers}
          permissions={permissions}
          associatedProjectsList={associatedProjectsList}
          showCheckboxes={true}
          selectedCollectionIds={selectedCollectionIds}
          onCollectionSelectionChange={handleCollectionSelectionChange}
          selectedProjectId={projectId}
          allProjects={[...ownedProjects, ...sharedProjects]}
        />
      </SelectionModal>

      <ProjectsSnackbarComponent />
    </>
  );
};